import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useTinyMCESlashAutocomplete from "~/components/Hooks/useTinyMCESlashAutocomplete";
import { useAvailability } from "~/repository";
import { getCalendarMembersByEventId } from "~/store/selectors/selector.calendars";
import { getProjectMembersById, getProjectLeaderById } from "~/store/selectors/selector.projects";
import { getSubscriberIdsByTypeAndId } from "~/store/selectors/selector.subscription";
import { getAssigneeId } from "~/store/selectors/selector.tasks";
import { makeTeamMentionsByUserIdsSelector } from "~/store/selectors/selector.teams";
import { makeGetUsersForMention } from "~/store/selectors/selector.users";

const useTinyMCEAutocomplete = (options = {}) => {
  const mentionDelimiter = `@`;
  const slashDelimiter = `/`;

  const {
    projectId = 0,
    parentType = "",
    parentId = 0,
    members = [],
    hideProject = false,
    hideEveryone = false,
    hideHere = false,
    allowedDelimiters = [mentionDelimiter, slashDelimiter],
  } = options;

  const { t } = useTranslation();

  const { data: unavailableUserIds } = useAvailability();

  const slashDelimiterAutocomplete = useTinyMCESlashAutocomplete();

  // get users
  const possibleUserIds = useSelector((state) => {
    if (parentType === "CalendarEvent") {
      return getCalendarMembersByEventId(state, parentId);
    }
    return getProjectMembersById(state, projectId);
  });

  const userIds = useMemo(() => {
    return [...new Set([...possibleUserIds, ...members])];
  }, [members, possibleUserIds]);

  const getUserMentions = useMemo(
    () => makeGetUsersForMention(userIds, unavailableUserIds),
    [unavailableUserIds, userIds]
  );
  const userMentions = useSelector(getUserMentions);
  const userMentionsRef = useRef(userMentions);

  // get project leader
  const projectLeaderId = useSelector((state) => getProjectLeaderById(state, projectId));
  const projectLeaderRef = useRef(projectLeaderId);

  // get team mentions
  const getTeamMentions = useMemo(() => {
    return makeTeamMentionsByUserIdsSelector(userIds);
  }, [userIds]);
  const teamMentions = useSelector((state) => getTeamMentions(state, projectId));
  const teamMentionsRef = useRef(teamMentions);

  // get context item subscribers
  const subscriberIds = useSelector((state) => getSubscriberIdsByTypeAndId(state, parentType, parentId));
  const subscriberIdsRef = useRef(subscriberIds);

  // Just for tasks get assignee id
  const assigneeId = useSelector((state) => {
    return parentId && parentType === "Task" ? getAssigneeId(state, parentId) : null;
  });
  const assigneeIdRef = useRef(assigneeId);

  useEffect(() => {
    userMentionsRef.current = userMentions;
    teamMentionsRef.current = teamMentions;
    subscriberIdsRef.current = subscriberIds;
    assigneeIdRef.current = assigneeId;
    projectLeaderRef.current = projectLeaderId;
  }, [userMentions, subscriberIds, assigneeId, projectLeaderId, teamMentions]);

  const source = (query, process, delimiter) => {
    if (delimiter === mentionDelimiter) {
      const userMentions = userMentionsRef.current.toList().toJS();
      const teamMentions = teamMentionsRef.current;
      const subscriberIds = subscriberIdsRef.current;
      const assigneeId = assigneeIdRef.current;
      const projectLeader = projectLeaderRef.current;

      const magicMentions = [];

      if (!hideProject) {
        magicMentions.push({
          type: "magic",
          name: "Project",
          user_ids: possibleUserIds,
        });
      }

      if (!hideEveryone) {
        magicMentions.push({
          type: "magic",
          name: "Everyone",
          user_ids: userIds,
        });
      }

      if (projectLeader) {
        magicMentions.push({
          type: "magic",
          name: "Leader",
          user_ids: projectLeader,
        });
      }

      if (parentType === "Task" && parentId) {
        if (assigneeId) {
          magicMentions.push({
            type: "magic",
            name: "Assignee",
            user_ids: assigneeId,
          });
        }

        magicMentions.push({
          type: "magic",
          name: "Task",
          user_ids: subscriberIds,
        });
      }

      if (parentType === "Note" && parentId) {
        magicMentions.push({
          type: "magic",
          name: "Note",
          user_ids: subscriberIds,
        });
      }

      if (parentType === "Discussion" && parentId) {
        magicMentions.push({
          type: "magic",
          name: "Discussion",
          user_ids: subscriberIds,
        });
      }

      if (parentType === "CalendarEvent" && parentId) {
        magicMentions.push({
          type: "magic",
          name: "Calendar",
          user_ids: userIds,
        });
      }

      if (parentType === "CalendarEvent" && parentId) {
        magicMentions.push({
          type: "magic",
          name: "Event",
          user_ids: subscriberIds,
        });
      }

      const hereParentTypes = ["Task", "Note", "Discussion", "CalendarEvent"];
      const isHereParentType = hereParentTypes.some((type) => type === parentType);

      if (!hideHere && isHereParentType && parentId) {
        magicMentions.push({
          type: "magic",
          name: "Here",
          user_ids: subscriberIds,
        });
      }

      const mentions = [...userMentions, ...teamMentions, ...magicMentions];

      if (mentions.length) {
        process(mentions.sort((a, b) => a.name.localeCompare(b.name)));
      }
    }

    slashDelimiterAutocomplete.source(query, process, delimiter);
  };

  const resolveMagicIcon = (item, parentType) => {
    let icon = "";
    switch (item.name) {
      case "Leader":
        return (icon = "leader");
      case "Assignee":
        return (icon = "assignee");
      case "Note":
        return (icon = "note");
      case "Discussion":
        return (icon = "discussion");
      case "Task":
        return (icon = "task");
      case "Event":
        return (icon = "calendar-event");
      case "Calendar":
        return (icon = "calendar");
      case "Here":
        if (parentType === "Note") {
          return "note";
        }
        if (parentType === "Discussion") {
          return "discussion";
        }
        if (parentType === "CalendarEvent") {
          return "calendar-event";
        }
        return "task";
      case "Everyone":
        if (parentType === "CalendarEvent") {
          return "calendar";
        }
        return (icon = "project");
      default:
        icon = "project";
    }
    return icon;
  };

  const render = (item, delimiter) => {
    if (delimiter === mentionDelimiter) {
      if (item.type === "user") {
        return item.unavailable
          ? `
              <li>
                <a href="javascript:;">
                  <div class="a51_autocomplete">
                    <div class="a51_autocomplete_option">
                      <img src="${item.avatar_url}" alt=""/>
                      <span class="name" title="${item.name}">${item.name}</span>
                    </div>
                    <span class="warning tw-text-red-600 tw-font-medium tw-flex tw-shrink-0 tw-whitespace-nowrap tw-items-center">${t(
                      "Unavailable"
                    )}</span>
                  </div>
                </a>
              </li>
            `
          : `
              <li>
                <a href="javascript:;">
                <div class="a51_autocomplete">
                    <div class="a51_autocomplete_option">
                      <img src="${item.avatar_url}" alt=""/>
                      <span class="name" title="${item.name}">${item.name}</span>
                    </div>
                  </div>
                </a>
              </li>
            `;
      } else if (item.type === "team") {
        return `
          <li>
            <a href="javascript:;">
              <div class="a51_autocomplete">
                <div class="a51_autocomplete_option">
                  <img src="${item.avatar_url}" alt=""/>
                  <span class="name">${item.name}</span>
                  <span class="members" title="${item.tooltip_text}">(${item.members_invited}/${item.members_count} ${t(
          "invited"
        )})</span>
                </div>
              </div>
            </a>
          </li>
        `;
      } else if (item.type === "magic") {
        const icon = resolveMagicIcon(item, parentType);
        return `
          <li>
            <a href="javascript:;">
              <div class="a51_autocomplete">
                <div class="a51_autocomplete_option">
                <span class="icon ${icon}">
                </span>
                  <span class="name">${item.name}</span>
                </div>
              </div>
            </a>
          </li>
        `;
      }
    }

    if (delimiter === slashDelimiter) {
      return slashDelimiterAutocomplete.render(item, delimiter);
    }
  };

  const insert = (item, delimiter) => {
    if (delimiter === mentionDelimiter) {
      if (item.type === "user") {
        return `<span class="new_mention" data-type="${
          item.type
        }" data-context="${parentType.toLowerCase()}" data-user-id="${item.id}">${item.name}</span>`;
      } else if (item.type === "team") {
        return `<span class="new_mention" data-type="${
          item.type
        }" data-context="${parentType.toLowerCase()}" data-user-id="${item.member_ids}">${item.name}</span>`;
      } else if (item.type === "magic") {
        return `<span class="new_mention" data-type="${item.name.toLowerCase()}" data-context="${parentType.toLowerCase()}" data-user-id="${
          item.user_ids
        }">${item.name}</span>`;
      }
    }

    if (delimiter === slashDelimiter) {
      return slashDelimiterAutocomplete.insert(item, delimiter);
    }
  };

  const buttonAction = (item, delimiter) => {
    if (delimiter === slashDelimiter) {
      return slashDelimiterAutocomplete.buttonAction(item, delimiter);
    }
  };

  const renderDropdown = () => `<ul class="rte-autocomplete"></ul>`;

  return {
    source,
    render,
    insert,
    buttonAction,
    renderDropdown,
    minChar: 0,
    delay: 0,
    delimiter: allowedDelimiters,
    queryBy: `name`,
    items: 10,
  };
};

export default useTinyMCEAutocomplete;
